import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { newKey, ServiceError } from '../api'
import { StatusPill, type PillTone } from '../carta/StatusPill'
import { readIngest, retryIngestPhase, type IngestView } from '../ingest'
import { moment } from '../format'
import { readSnapshot, writeSnapshot } from '../readSnapshots'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'

interface Loaded {
  ingest: IngestView
  snapshotAt: string | null
}

const PHASE_TONE: Record<string, PillTone> = {
  queued: 'ink-3',
  running: 'ref',
  done: 'sev-0',
  failed: 'sev-4',
}

const PHASE_KEY: Record<string, MessageKey> = {
  queued: 'pool.statusQueued',
  running: 'pool.statusProcessing',
  done: 'ingest.phaseDone',
  failed: 'pool.statusError',
}

/** Le fasi che il catalogo sa nominare: le stesse di `INGEST_PHASES` nel
 *  servizio, e `tests/test_explained_codes.py` tiene le due liste allineate. */
const NAMED_PHASES = new Set([
  'verify',
  'inventory',
  'passport',
  'telemetry',
  'media',
  'binding',
  'coverage',
  'analysis',
  'inference',
  'report',
])

function phaseName(t: (key: MessageKey) => string, name: string): string {
  return NAMED_PHASES.has(name) ? t(`ingest.phase.${name}.name` as MessageKey) : name
}

function phaseSays(t: (key: MessageKey) => string, name: string): string | null {
  return NAMED_PHASES.has(name) ? t(`ingest.phase.${name}.says` as MessageKey) : null
}

export function IngestMonitor(): JSX.Element {
  const t = useT()
  const jobId = useParams().jobId ?? ''
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  async function load(): Promise<void> {
    setFailure(null)
    try {
      const ingest = await readIngest(jobId)
      setLoaded({ ingest, snapshotAt: null })
      void writeSnapshot('ingest', jobId, ingest).catch(() => undefined)
      return
    } catch (error) {
      const snapshot = await readSnapshot<IngestView>('ingest', jobId).catch(() => null)
      if (snapshot !== null) {
        setLoaded({ ingest: snapshot.value, snapshotAt: snapshot.captured_at })
        return
      }
      setFailure(error instanceof ServiceError ? error.message : t('ingest.unread'))
    }
  }

  useEffect(() => {
    void load().catch((error) => {
      setFailure(error instanceof ServiceError ? error.message : t('ingest.unread'))
    })
  }, [jobId])

  async function retry(phase: string): Promise<void> {
    if (loaded?.snapshotAt !== null) return
    const response = await retryIngestPhase(jobId, phase, newKey())
    setMessage(response.message)
    await load()
  }

  if (failure !== null) {
    return (
      <main>
        <h2>{t('ingest.unavailable')}</h2>
        <p role="alert">{failure}</p>
      </main>
    )
  }

  if (loaded === null) {
    return (
      <main>
        <p role="status">{t('ingest.reading')}</p>
      </main>
    )
  }

  const readonly = loaded.snapshotAt !== null
  const retryables = loaded.ingest.phases.filter((phase) => phase.retryable)
  return (
    <main>
      <h2>{t('title.ingest')}</h2>
      {readonly && (
        <p role="note">{t('ingest.offline', { when: moment(loaded.snapshotAt ?? '') })}</p>
      )}
      {message !== null && <p role="status">{message}</p>}
      <p>
        {t('ingest.job')} <code>{loaded.ingest.job_id}</code> — {t('ingest.state')}{' '}
        <strong>{loaded.ingest.state}</strong>
      </p>
      <ul>
        {loaded.ingest.phases.map((phase) => {
          const phaseKey = PHASE_KEY[phase.state]
          return (
          <li key={phase.name}>
            {/* Il nome della fase per chi la legge, e una riga su che cosa fa: il
                monitor mostrava `binding` e `passport` come stanno nel codice
                (GI-281). Una fase che il catalogo non conosce resta col suo
                nome interno, che e' vero anche se non e' bello. */}
            <strong>{phaseName(t, phase.name)}</strong>
            {phaseSays(t, phase.name) !== null && <span className="tenue"> — {phaseSays(t, phase.name)}</span>}:{' '}
            <StatusPill
              label={phaseKey !== undefined ? t(phaseKey) : phase.state}
              tone={PHASE_TONE[phase.state] ?? 'ink-3'}
              pulse={phase.state === 'running'}
            />
            , {t('ingest.attempts')} <span className="t-num">{phase.attempts}</span>
            {phase.last_error && (
              <>
                {' '}
                — <span role="alert">{phase.last_error}</span>
              </>
            )}
            {!readonly && phase.retryable && (
              <>
                {' '}
                <button
                  type="button"
                  className={retryables.length === 1 ? 'primario' : undefined}
                  onClick={() => void retry(phase.name)}
                >
                  {t('ingest.retry')}
                </button>
              </>
            )}
          </li>
          )
        })}
      </ul>
    </main>
  )
}
